import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SidebarComponent } from "./sidebar/sidebar.component";
import { NavigationEnd, RouteReuseStrategy, Router, RouterOutlet } from '@angular/router';
import { HeaderComponent } from "./header/header.component";
import { filter, Subscription } from 'rxjs';
import { TabWorkspaceService, MAX_TABS } from './tab-workspace.service';
import { TabRouteReuseStrategy } from './tab-route-reuse.strategy';
import { ToastrService } from 'ngx-toastr';
import { RuntimeService } from '../shared/services/runtime.service';
import { CdkScrollableModule } from '@angular/cdk/scrolling';

@Component({
  selector: 'app-layout',
  imports: [
    CommonModule,
    SidebarComponent,
    RouterOutlet,
    HeaderComponent,
    CdkScrollableModule
  ],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss'
})
export class LayoutComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly workspace = inject(TabWorkspaceService);
  private readonly routeReuseStrategy = inject(RouteReuseStrategy);
  private readonly toastr = inject(ToastrService);
  private navigationSubscription?: Subscription;
  readonly isWeb = inject(RuntimeService).isWeb;
  readonly hideLayout = signal(false);

  get isLoggedIn() {
    const token = localStorage.getItem('auth_token');
    const resetRequired = localStorage.getItem('reset_password_required') === 'true';
    const mfaPending = localStorage.getItem('mfa_pending') === 'true';
    return !!token && !resetRequired && !mfaPending;
  }

  readonly tabs = this.workspace.tabs;
  readonly activeTabId = this.workspace.activeTabId;

  ngOnInit(): void {
    this.workspace.syncWithUrl(this.router.url);
    this.navigationSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.workspace.syncWithUrl(event.urlAfterRedirects);
        this.updateHideLayout();
      });
    this.updateHideLayout();
  }

  private updateHideLayout(): void {
    const url = this.router.url.split('?')[0];
    // Layout is a shell (not a routed parent), so ActivatedRoute.firstChild can
    // stay on the previous workspace route after a returning login → /data-sync.
    // Hide chrome whenever this URL is active, not only when route data is found.
    if (url.startsWith('/data-sync')) {
      this.hideLayout.set(true);
      return;
    }

    let route = this.router.routerState.snapshot.root;
    while (route.firstChild) route = route.firstChild;
    this.hideLayout.set(!!route.data['hideLayout']);
  }

  ngOnDestroy(): void {
    this.navigationSubscription?.unsubscribe();
  }

  async addTab(): Promise<void> {
    const nextTab = this.workspace.addTab(this.router.url);
    if (!nextTab) {
      this.toastr.warning(`Maximum ${MAX_TABS} tabs are allowed.`, 'Tab Limit Reached');
      return;
    }
    await this.router.navigateByUrl(nextTab.url);
  }

  async switchTab(tabId: string): Promise<void> {
    const nextUrl = this.workspace.requestTabSwitch(tabId);
    if (!nextUrl) {
      return;
    }

    await this.router.navigateByUrl(nextUrl);
  }

  async closeTab(tabId: string): Promise<void> {
    const { navigateTo, removedTabId } = this.workspace.closeTab(tabId);
    const strategy = this.routeReuseStrategy as TabRouteReuseStrategy;
    strategy.clearTab(removedTabId);

    if (navigateTo) {
      await this.router.navigateByUrl(navigateTo);
    }
  }
  get isChatRoute(): boolean {
    return this.router.url.includes('/chat');
  }

}
